import { useState } from 'react'

import { useAtomValue } from 'jotai'
import {
  Text,
  GreyGrey80,
  Primary13Regular,
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import {
  useActivateAutomationVersion,
  useAutomationId,
  useSaveAndActivateAutomationVersion
} from 'Automations/automationEditor/hooks'
import {
  editingVersionIdAtom,
  hasAnyChangesAtom,
  hasPresentationChangedAtom
} from 'Automations/automationEditor/atoms'
import { DuplicateTriggerWarningBanner } from 'Automations/automationsManagement/components/WorkflowCard/DuplicateTriggerWarningBanner'
import { ChangelogField } from './ChangelogField'

type TriggerType = Dashboardv99990101Api.Automations.TriggerType

type Conflict = {
  automationId: string
  automationName: string
}

export const ModalActivateVersion = ({
  onClose,
  conflict,
  triggerType
}: {
  onClose: () => void
  conflict?: Conflict | null
  triggerType?: TriggerType
}) => {
  const { displayNotification } = useNotifications()

  const { automationId, isNewAutomation: _isNewAutomation } = useAutomationId()
  const [isNewAutomation] = useState(_isNewAutomation)
  const activateVersion = useActivateAutomationVersion()
  const saveAndActivateVersion = useSaveAndActivateAutomationVersion()

  const editingVersionId = useAtomValue(editingVersionIdAtom)
  const [hasAnyChanges] = useState(useAtomValue(hasAnyChangesAtom))
  const [hasPresentationChanged] = useState(
    useAtomValue(hasPresentationChangedAtom)
  )

  const [changelog, setChangelog] = useState<string>('')

  const showChangelogField: boolean = !isNewAutomation && hasPresentationChanged

  return (
    <SimpleModal
      variant="dialog"
      isOpen
      onClose={onClose}
      width="436px"
      title="Publish workflow"
      data-testid="automations.editor.publishModal"
      onSubmit={async () => {
        try {
          if (hasAnyChanges) {
            await saveAndActivateVersion({ changelog })
          } else {
            if (!automationId) throw new Error('automationId is undefined')
            if (!editingVersionId) {
              throw new Error('editingVersionId is undefined')
            }
            await activateVersion({ automationId, versionId: editingVersionId })
          }

          displayNotification('Workflow published', { type: 'success' })
          return true
        } catch (error) {
          console.error(error)
          displayNotification('Could not publish workflow', { type: 'error' })
          return false
        }
      }}
      submitButtonText="Publish"
      submittingButtonText="Publishing..."
      disableSubmitOnEnter
    >
      <Flex flexDir="column" gap="16px">
        {conflict && triggerType && (
          <DuplicateTriggerWarningBanner
            conflictingAutomationId={conflict.automationId}
            conflictingAutomationName={conflict.automationName}
            triggerType={triggerType}
          />
        )}

        <Text
          {...Primary13Regular}
          color={GreyGrey80}
          data-testid="automations.editor.publishModal.description"
        >
          {hasPresentationChanged
            ? `Are you sure you want to publish this workflow? Your changes will be saved as a new version.`
            : `Are you sure you want to publish this workflow?`}
        </Text>

        {/* First version created via new automation has a default changelog value */}
        {/* Don't show field when activating an already saved version */}
        {showChangelogField && (
          <ChangelogField changelog={changelog} setChangelog={setChangelog} />
        )}
      </Flex>
    </SimpleModal>
  )
}
